import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import api from '../../api';
import { useAdmin } from '../../context/AdminContext';

const blank = {
    name: '',
    country: '',
    heroImage: '',
    overview: '',
    highlights: '',
    bestTimeToVisit: '',
    description: '',
    isFeatured: true,
    isPublished: true
  };

export default function AdminDestinations() {
  const [destinations, setDestinations] = useState([]);
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [destChecked, setDestChecked] = useState([]);
  const { isSuperAdmin } = useAdmin();

  const load = function() {
    api.get('/destinations/all').then(function(res) { setDestinations(res.data); setDestChecked([]); }).catch(function() {});
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    // highlights is entered as comma-separated text, convert to array
    const payload = {
      ...form,
      highlights: form.highlights
        ? form.highlights.split(',').map((h) => h.trim()).filter(Boolean)
        : []
    };
    try {
      if (editingId) {
        await api.put(`/destinations/${editingId}`, payload);
      } else {
        await api.post('/destinations', payload);
      }
      setForm(blank);
      setEditingId(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save destination');
    }
  };

  const startEdit = (d) => {
    setForm({
      name: d.name,
      country: d.country || '',
      heroImage: d.heroImage || '',
      overview: d.overview || '',
      highlights: (d.highlights || []).join(', '),
      bestTimeToVisit: d.bestTimeToVisit || '',
      description: d.description || '',
      isFeatured: d.isFeatured,
      isPublished: d.isPublished
    });
    setEditingId(d._id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this destination?')) return;
    await api.delete(`/destinations/${id}`);
    load();
  };

  return (
    <AdminLayout>
      <h1>Destinations</h1>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24, marginTop: 20 }}>

        {/* FORM */}
        <form onSubmit={handleSubmit} className="card" style={{ padding: 24 }}>
          <h3>{editingId ? 'Edit Destination' : 'Add Destination'}</h3>

          <div className="form-field" style={{ marginBottom: 12 }}>
            <label>Name</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Dubai" />
          </div>

          <div className="form-field" style={{ marginBottom: 12 }}>
            <label>Country</label>
            <input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} placeholder="e.g. UAE" />
          </div>

          <div className="form-field" style={{ marginBottom: 12 }}>
            <label>Hero Image URL</label>
            <input value={form.heroImage} onChange={(e) => setForm({ ...form, heroImage: e.target.value })} placeholder="https://..." />
            {form.heroImage && (
              <img src={form.heroImage} alt="preview" style={{ marginTop: 8, height: 100, objectFit: 'cover', borderRadius: 6 }} />
            )}
          </div>

          <div className="form-field" style={{ marginBottom: 12 }}>
            <label>Overview</label>
            <textarea rows={4} value={form.overview} onChange={(e) => setForm({ ...form, overview: e.target.value })} placeholder="A short description of this destination..." />
          </div>

          <div className="form-field" style={{ marginBottom: 12 }}>
            <label>Highlights <span style={{ fontWeight: 400, opacity: 0.6 }}>(comma-separated)</span></label>
            <input value={form.highlights} onChange={(e) => setForm({ ...form, highlights: e.target.value })} placeholder="Burj Khalifa, Desert Safari, Dubai Mall" />
          </div>

          <div className="form-field" style={{ marginBottom: 12 }}>
            <label>Best Time to Visit</label>
            <input value={form.bestTimeToVisit} onChange={(e) => setForm({ ...form, bestTimeToVisit: e.target.value })} placeholder="e.g. November to March" />
          </div>

          <div className="form-field" style={{ marginBottom: 12 }}>
            <label>Detailed Description <span style={{ fontWeight: 400, opacity: 0.6 }}>(supports paragraphs — press Enter twice between them)</span></label>
            <textarea rows={7} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Write a detailed guide about this destination — culture, food, must-see areas, travel tips..." />
          </div>

          <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={form.isFeatured} onChange={(e) => setForm({ ...form, isFeatured: e.target.checked })} />
              Featured on homepage
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={form.isPublished} onChange={(e) => setForm({ ...form, isPublished: e.target.checked })} />
              Published
            </label>
          </div>

          {error && <p className="form-message error">{error}</p>}

          <button className="btn btn-primary" style={{ width: '100%' }}>
            {editingId ? 'Update Destination' : 'Add Destination'}
          </button>
          {editingId && (
            <button type="button" className="btn btn-outline" style={{ width: '100%', marginTop: 8 }}
              onClick={() => { setForm(blank); setEditingId(null); setError(''); }}>
              Cancel Edit
            </button>
          )}
        </form>

        {/* LIST */}
        <div className="card" style={{ padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
            <h3 style={{ margin: 0 }}>All Destinations ({destinations.length})</h3>
            {destinations.length > 0 && isSuperAdmin() &&(
              <button
                className="btn btn-outline"
                style={{ padding: '5px 12px', fontSize: '0.8rem', borderColor: '#ef4444', color: '#ef4444' }}
                onClick={async function() {
                  if (!window.confirm('Delete ALL ' + destinations.length + ' destinations? This cannot be undone.')) return;
                  await Promise.all(destinations.map(function(d) { return api.delete('/destinations/' + d._id); }));
                  load();
                }}
              >
                🗑 Delete All
              </button>
            )}
          </div>

          {/* Select + bulk delete row */}
          {destinations.length > 0 && isSuperAdmin() &&(
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, padding: '8px 0', borderBottom: '1px solid var(--bj-sand)' }}>
              <input
                type="checkbox"
                style={{ accentColor: 'var(--bj-clay)', width: 16, height: 16 }}
                checked={destChecked.length === destinations.length && destinations.length > 0}
                onChange={function(e) {
                  if (e.target.checked) {
                    setDestChecked(destinations.map(function(d) { return d._id; }));
                  } else {
                    setDestChecked([]);
                  }
                }}
              />
              <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Select all</span>
              {destChecked.length > 0 && isSuperAdmin() &&(
                <button
                  className="btn btn-outline"
                  style={{ marginLeft: 'auto', padding: '4px 12px', fontSize: '0.8rem', borderColor: '#ef4444', color: '#ef4444' }}
                  onClick={async function() {
                    if (!window.confirm('Delete ' + destChecked.length + ' selected destinations?')) return;
                    await Promise.all(destChecked.map(function(id) { return api.delete('/destinations/' + id); }));
                    setDestChecked([]);
                    load();
                  }}
                >
                  Delete Selected ({destChecked.length})
                </button>
              )}
            </div>
          )}
          {destinations.length === 0 && <p>No destinations yet — add your first one.</p>}
          {destinations.map((d) => (
            <div key={d._id} style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--bj-sand)', background: destChecked.includes(d._id) ? 'rgba(63,114,175,0.04)' : 'transparent', borderRadius: 6, padding: '10px 6px' }}>
                <input
                  type="checkbox"
                  checked={destChecked.includes(d._id)}
                  onChange={function() {
                    setDestChecked(function(prev) {
                      return prev.includes(d._id) ? prev.filter(function(x) { return x !== d._id; }) : [...prev, d._id];
                    });
                  }}
                  style={{ accentColor: 'var(--bj-clay)', width: 16, height: 16, flexShrink: 0 }}
                />
              {d.heroImage && (
                <img src={d.heroImage} alt={d.name} style={{ width: 60, height: 45, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />
              )}
              <div style={{ flex: 1 }}>
                <strong>{d.name}</strong>
                {d.country && <span style={{ opacity: 0.6, fontSize: '0.85rem' }}> · {d.country}</span>}
                <div style={{ fontSize: '0.78rem', marginTop: 2, display: 'flex', gap: 8 }}>
                  {d.isFeatured && <span style={{ color: 'var(--bj-clay)', fontWeight: 600 }}>⭐ Featured</span>}
                  {d.isPublished
                    ? <span style={{ color: 'var(--bj-success)', fontWeight: 600 }}>✅ Published</span>
                    : <span style={{ color: 'var(--bj-error)', fontWeight: 600 }}>❌ Hidden from public</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button className="btn btn-outline" style={{ padding: '4px 10px' }} onClick={() => startEdit(d)}>Edit</button>
                {isSuperAdmin() && (<button className="btn btn-outline" style={{ padding: '4px 10px' }} onClick={() => remove(d._id)}>Delete</button>)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}